import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Text,
  UnstyledButton,
  Group,
  Stack,
  Button,
  ScrollArea,
  Flex,
  Box,
  SimpleGrid,
  Tooltip,
} from '@mantine/core';
import { IconSearch, IconCircle, IconCircleCheckFilled } from '@tabler/icons-react';
import { primaryColor } from '../../constants/theme';
import { CategoryResponseModel } from '../../types/Profile';
import { IdNamePair } from './types';

type CategoriesModalProps = {
  opened: boolean;
  categories: CategoryResponseModel[];
  selectedCategories: IdNamePair[];
  onToggleCategory: (category: IdNamePair) => void;
  onSave: () => void;
  onCancel: () => void;
};

export const CategoriesModal: React.FC<CategoriesModalProps> = ({
  opened,
  categories,
  selectedCategories,
  onToggleCategory,
  onSave,
  onCancel,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Reset search query when modal opens
  useEffect(() => {
    if (opened) {
      setSearchQuery('');
    }
  }, [opened]);

  const filteredCategories = useMemo(() => {
    const query = (searchQuery || '').trim().toLowerCase();
    if (!query) return categories;
    return categories.filter((cat) =>
      (cat.name || '').toLowerCase().indexOf(query) !== -1
    );
  }, [categories, searchQuery]);

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={<Text fw={600} size="xl">Select Categories</Text>}
      size="lg"
      radius="md"
      centered
      padding="xl"
      data-testid="categories-modal"
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search categories..."
          leftSection={<IconSearch size={20} color="gray" />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          radius="md"
          size="lg"
          styles={{ input: { fontSize: 'var(--mantine-font-size-md)' } }}
        />

        
        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="sm">
          {searchQuery ? 'Search Results' : 'All Categories'}
        </Text>

        <ScrollArea h={350}>
          {filteredCategories.length > 0 ? (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" pr={{ base: 0, sm: 'sm' }}>
              {filteredCategories.map((category) => {
                const isSelected = selectedCategories.some((c) => c.id === category.id);
                return (
                  <UnstyledButton
                    key={category.id}
                    onClick={() => onToggleCategory(category)}
                    data-testid={`category-option-${category.id}`}
                    style={{
                      border: `1px solid ${
                        isSelected ? '#14B885' : 'var(--mantine-color-gray-3)'
                      }`,
                      borderRadius: 'var(--mantine-radius-md)',
                      padding: '12px 16px',
                      backgroundColor: isSelected ? '#E6F9F0' : 'transparent',
                      transition: 'all 0.2s ease',
                      width: '100%',
                    }}
                  >
                    <Flex justify="space-between" align="center" wrap="nowrap">
                      <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        <Tooltip label={category.name} openDelay={500} withArrow>
                          <Text fw={500} size="md" c={isSelected ? 'dark.9' : 'dark.7'} truncate>
                            {category.name}
                          </Text>
                        </Tooltip>
                      </Group>
                      <Box style={{ flexShrink: 0, marginLeft: '8px' }}>
                        {isSelected ? (
                          <IconCircleCheckFilled size={24} color="#14B885" />
                        ) : (
                          <IconCircle size={24} color="var(--mantine-color-gray-4)" />
                        )}
                      </Box>
                    </Flex>
                  </UnstyledButton>
                );
              })}
            </SimpleGrid>
          ) : (
            <Text c="dimmed" ta="center" py="xl">
              No categories found
            </Text>
          )}
        </ScrollArea>

        <Group grow gap="md" mt="md">
          <Button
            onClick={onSave}
            color={primaryColor}
            radius="md"
            size="md"
            data-testid="save-categories-button"
          >
            Save
          </Button>
          <Button
            onClick={onCancel}
            variant="outline"
            color={primaryColor}
            radius="md"
            size="md"
            data-testid="cancel-categories-button"
            styles={{
              root: {
                borderWidth: 1,
              }
            }}
          >
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
